import {useSideBarAccordion} from '@/contexts/SideBarAccordionContext';
import {useFeatures} from '@/hooks/useFeatures/useFeatures';
import {useLogAppUse} from '@/hooks/useLogAppUse';
import {useTrackPageView} from '@/hooks/useTrackPageView';
import {LayoutSwitch} from '@/layout/LayoutSwitch';
import {SentryRoutes} from '@/main';
import {useGlobalFeatures} from '@ark/react';
import {useIsMobile} from '@ark/web';
import React, {useEffect} from 'react';
import lazyWithPreload from 'react-lazy-with-preload';
import {Navigate, Route} from 'react-router-dom';
import {ParamsToUUIDNavigator} from '../ParamsToUUIDNavigator';
import {useHasSafetyCenterAccess} from './ManagerSidebarContent';
import {useReferencingEnabled} from './useReferencingEnabled/useReferencingEnabled';
import {importWithRetry, preloadAll} from '@/utils/chunkReloadOperations';

const ConversationsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Conversations/List/ConversationsPage').then(
      module => ({
        default: module.ConversationsPage,
      }),
    ),
  ),
);
const ConversationsPageV2Style = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '../../pages/ENT/Conversations/ConversationPageV2Style/ConversationsPage'
    ).then(module => ({
      default: module.ConversationsPage,
    })),
  ),
);
const DashboardPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Dashboard/DashboardPage/DashboardPage').then(
      module => ({
        default: module.DashboardPage,
      }),
    ),
  ),
);

const CommunityListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Communities/List/CommunityListPage').then(
      module => ({
        default: module.CommunityListPage,
      }),
    ),
  ),
);
const CommunityDetailsPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Communities/Tabs/CommunityDetailsPage').then(
      module => ({
        default: module.CommunityDetailsPage,
      }),
    ),
  ),
);
const SatisfactionScores = lazyWithPreload(() =>
  importWithRetry(
    () => import('../../pages/ENT/Dashboard/SatisfactionScoresPage'),
  ),
);
const DownloadRate = lazyWithPreload(() =>
  importWithRetry(() => import('../../pages/ENT/Dashboard/DownloadRatePage')),
);
const ResidentEngagement = lazyWithPreload(() =>
  importWithRetry(() => import('../../pages/ENT/Dashboard/ResidentEngagement')),
);
const ResponseTime = lazyWithPreload(() =>
  importWithRetry(() => import('../../pages/ENT/Dashboard/ResponseTimePage')),
);
const ContactPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Contacts/Detail/ContactDetailsPage').then(
      module => ({
        default: module.ContactDetailsPage,
      }),
    ),
  ),
);

const ContactsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Contacts/List/ContactsListPage').then(module => ({
      default: module.ContactsListPage,
    })),
  ),
);
const ApplicantsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Applicants/List/ApplicantsListPage').then(
      module => ({
        default: module.ApplicantsListPage,
      }),
    ),
  ),
);
const ApplicantPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Applicants/detail/ApplicantPage').then(module => ({
      default: module.ApplicantPage,
    })),
  ),
);
const ContractorsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Contractors/List/ContractorsListPage').then(
      module => ({
        default: module.ContractorsListPage,
      }),
    ),
  ),
);
const ProfilePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/ProfilePage/ProfilePage').then(module => ({
      default: module.ProfilePage,
    })),
  ),
);
const PackagesPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Packages/List/PackagesPage').then(module => ({
      default: module.PackagesPage,
    })),
  ),
);
const KeysListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Keys/List/KeysListPage').then(module => ({
      default: module.KeysListPage,
    })),
  ),
);
const CommunityProductsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '../../pages/ENT/CommunityProducts/List/CommunityProductsListPage'
    ).then(module => ({
      default: module.CommunityProductsListPage,
    })),
  ),
);
const HomePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Homes/Detail/HomeDetailsPage').then(module => ({
      default: module.HomeDetailsPage,
    })),
  ),
);
const HomesListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Homes/List/HomesListPage').then(module => ({
      default: module.HomesListPage,
    })),
  ),
);
const SafetyCenterPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/SafetyCenter/Occupants/SafetyCenterPage').then(
      module => ({
        default: module.SafetyCenterPage,
      }),
    ),
  ),
);
const SafetyCenterRecordPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/SafetyCenter/Record/SafetyCenterRecordPage').then(
      module => ({
        default: module.SafetyCenterRecordPage,
      }),
    ),
  ),
);
const LegacyDocumentsRedirect = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '../../pages/ENT/SafetyCenter/Documents/LegacyDocumentsRedirect'
    ).then(module => ({default: module.LegacyDocumentsRedirect})),
  ),
);
const SafetyOverviewListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '../../pages/ENT/SafetyCenter/Overview/Portfolio/SafetyOverviewListPage'
    ).then(module => ({default: module.SafetyOverviewListPage})),
  ),
);
const SafetyOverviewPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/SafetyCenter/Overview/SafetyOverviewPage').then(
      module => ({default: module.SafetyOverviewPage}),
    ),
  ),
);
const InspectionsPage = lazyWithPreload(() =>
  import('../../pages/ENT/SafetyCenter/Inspections/InspectionsPage').then(
    module => ({
      default: module.InspectionsPage,
    }),
  ),
);
const InspectionDetailPage = lazyWithPreload(() =>
  import('../../pages/ENT/SafetyCenter/Inspections/InspectionDetailPage').then(
    module => ({
      default: module.InspectionDetailPage,
    }),
  ),
);
const MemberListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Members/List/MemberListPage').then(module => ({
      default: module.MemberListPage,
    })),
  ),
);
const NewMemberPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Members/NewMemberPage/NewMemberPage').then(
      module => ({
        default: module.NewMemberPage,
      }),
    ),
  ),
);
const ViewMemberPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Members/ViewMemberPage/ViewMemberPage').then(
      module => ({
        default: module.ViewMemberPage,
      }),
    ),
  ),
);

const TeamListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Teams/List/TeamListPage').then(module => ({
      default: module.TeamListPage,
    })),
  ),
);

const ViewTeamPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Teams/Tabs/ViewTeamPage').then(module => ({
      default: module.ViewTeamPage,
    })),
  ),
);

const SettingsPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Settings/Settings').then(module => ({
      default: module.Settings,
    })),
  ),
);
const DocumentsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Documents/List/DocumentsListPage').then(module => ({
      default: module.DocumentsListPage,
    })),
  ),
);
const PageNotFound = lazyWithPreload(() =>
  importWithRetry(() => import('../../pages/ENT/404Page')),
);
const EventsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Events/List/EventsListPage').then(module => ({
      default: module.EventsListPage,
    })),
  ),
);
const PaymentsPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Payments/List/PaymentsPage').then(module => ({
      default: module.PaymentsPage,
    })),
  ),
);
const AmenitiesListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Amenities/List/AmenitiesListPage').then(module => ({
      default: module.AmenitiesListPage,
    })),
  ),
);
const AppointmentsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Appointments/List/AppointmentsListPage').then(
      module => ({
        default: module.AppointmentsListPage,
      }),
    ),
  ),
);
const NotificationListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Notifications/List/NotificationListPage').then(
      module => ({
        default: module.NotificationListPage,
      }),
    ),
  ),
);
const NotificationCenterPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Notifications/NotificationCenterPage').then(
      module => ({
        default: module.NotificationCenterPage,
      }),
    ),
  ),
);
const Referencing = lazyWithPreload(() =>
  importWithRetry(() => import('../../pages/ENT/Referencing')),
);
const ApplicationDetails = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Referencing/ApplicationDetailsPage').then(
      module => ({
        default: module.ApplicationDetailsPage,
      }),
    ),
  ),
);
const ApplicantDetails = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '../../pages/ENT/Referencing/components/ApplicantDetails/ApplicantDetails'
    ).then(module => ({
      default: module.ApplicantDetails,
    })),
  ),
);
const VisitorsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Visitors/List/VisitorsListPage').then(module => ({
      default: module.VisitorsListPage,
    })),
  ),
);

const GroupsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Groups/List/GroupsListPage').then(module => ({
      default: module.GroupsListPage,
    })),
  ),
);

const HomesPackagesPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Packages/Detail/HomePackagesPage').then(module => ({
      default: module.HomePackagesPage,
    })),
  ),
);

const ComplianceBlocksListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '../../pages/ENT/Compliance/Blocks/List/ComplianceBlocksListPage'
    ).then(module => ({
      default: module.ComplianceBlocksListPage,
    })),
  ),
);

const BlocksListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Blocks/List/BlockListPage').then(module => ({
      default: module.BlocksListPage,
    })),
  ),
);

const ViewBlockPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Blocks/Tabs/ViewBlockPage').then(module => ({
      default: module.ViewBlockPage,
    })),
  ),
);

const BulkAssignHomesPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Blocks/Tabs/BulkAssignHomesPage').then(module => ({
      default: module.BulkAssignHomesPage,
    })),
  ),
);

const RequirementsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '../../pages/ENT/Compliance/Requirements/List/RequirementsListPage'
    ).then(module => ({
      default: module.RequirementListPage,
    })),
  ),
);

const FormsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Forms/List/FormsListPage').then(module => ({
      default: module.FormsListPage,
    })),
  ),
);

const FeaturesPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Features/List/FeaturesPage').then(module => ({
      default: module.FeaturesPage,
    })),
  ),
);

const TagListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Tags/List/TagListPage').then(module => ({
      default: module.TagListPage,
    })),
  ),
);

const ViewTagPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Tags/Tabs/ViewTagPage').then(module => ({
      default: module.ViewTagPage,
    })),
  ),
);

const NewTeamPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Teams/NewTeamPage/NewTeamPage').then(module => ({
      default: module.NewTeamPage,
    })),
  ),
);

const NewsFeedPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Updates/List/NewsFeedPage').then(module => ({
      default: module.NewsFeedPage,
    })),
  ),
);

const SurveysListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Surveys/List/SurveysListPage').then(module => ({
      default: module.SurveysListPage,
    })),
  ),
);

const MarketplaceListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Marketplace/List/MarketplaceListPage').then(
      module => ({
        default: module.MarketplaceListPage,
      }),
    ),
  ),
);

const PulsePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Pulse/List/PulsePage').then(module => ({
      default: module.PulsePage,
    })),
  ),
);
const RulesPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Rules/List/RulesPage').then(module => ({
      default: module.RulesPage,
    })),
  ),
);

const TasksPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Tasks/List/TasksPage').then(module => ({
      default: module.TasksPage,
    })),
  ),
);

const ViewTaskPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Tasks/Tabs/ViewTaskPage').then(module => ({
      default: module.ViewTaskPage,
    })),
  ),
);

const ScheduledTasksPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Tasks/ScheduledTasksPage/ScheduledTasksPage').then(
      module => ({
        default: module.ScheduledTasksPage,
      }),
    ),
  ),
);

const ViewTaskGroupPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '../../pages/ENT/Tasks/ScheduledTasksPage/ViewTaskGroupPage/ViewTaskGroupPage'
    ).then(module => ({
      default: module.ViewTaskGroupPage,
    })),
  ),
);

const TaskTemplatesPage = lazyWithPreload(() =>
  import('../../pages/ENT/Tasks/Templates/TaskTemplatesPage').then(module => ({
    default: module.TaskTemplatesPage,
  })),
);

const TaskTemplateViewPage = lazyWithPreload(() =>
  import('../../pages/ENT/Tasks/Templates/TaskTemplateViewPage').then(
    module => ({
      default: module.TaskTemplateViewPage,
    }),
  ),
);

const ViewRulePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Rules/Tabs/ViewRulePage').then(module => ({
      default: module.ViewRulePage,
    })),
  ),
);

const NewRulePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Rules/NewRulePage/NewRulePage').then(module => ({
      default: module.NewRulePage,
    })),
  ),
);

const SiteReportingPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Reporting/SiteReporting/SiteReportingPage').then(
      module => ({
        default: module.SiteReportingPage,
      }),
    ),
  ),
);

const PortfolioReportingPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Reporting/Portfolio/PortfolioReportingPage').then(
      module => ({
        default: module.PortfolioReportingPage,
      }),
    ),
  ),
);

const TeamReportingPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Reporting/TeamReporting/TeamReportingPage').then(
      module => ({
        default: module.TeamReportingPage,
      }),
    ),
  ),
);

const SavedViewsPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Reporting/SavedViews/SavedViewsPage').then(
      module => ({
        default: module.SavedViewsPage,
      }),
    ),
  ),
);

const SavedViewPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Reporting/SavedViews/SavedViewPage').then(
      module => ({
        default: module.SavedViewPage,
      }),
    ),
  ),
);

const componentsToPreload = [
  ConversationsListPage,
  ConversationsPageV2Style,
  DashboardPage,
  CommunityDetailsPage,
  CommunityListPage,
  SatisfactionScores,
  DownloadRate,
  ResidentEngagement,
  ResponseTime,
  ContactsListPage,
  ContactPage,
  ApplicantsListPage,
  ProfilePage,
  PackagesPage,
  KeysListPage,
  CommunityProductsListPage,
  HomePage,
  HomesListPage,
  SafetyCenterPage,
  SafetyCenterRecordPage,
  LegacyDocumentsRedirect,
  SafetyOverviewListPage,
  SafetyOverviewPage,
  MemberListPage,
  NewMemberPage,
  ViewMemberPage,
  TeamListPage,
  ViewTeamPage,
  TagListPage,
  SettingsPage,
  DocumentsListPage,
  PageNotFound,
  EventsListPage,
  PaymentsPage,
  AmenitiesListPage,
  AppointmentsListPage,
  Referencing,
  ApplicationDetails,
  ApplicantDetails,
  VisitorsListPage,
  GroupsListPage,
  HomesPackagesPage,
  ComplianceBlocksListPage,
  RequirementsListPage,
  BlocksListPage,
  ViewBlockPage,
  BulkAssignHomesPage,
  FormsListPage,
  NotificationListPage,
  NotificationCenterPage,
  FeaturesPage,
  ViewTagPage,
  NewsFeedPage,
  NewTeamPage,
  SurveysListPage,
  MarketplaceListPage,
  PulsePage,
  RulesPage,
  ViewRulePage,
  NewRulePage,
  TasksPage,
  ViewTaskPage,
  ScheduledTasksPage,
  ViewTaskGroupPage,
  TaskTemplatesPage,
  TaskTemplateViewPage,
  ContractorsListPage,
  ApplicantPage,
];

type Props = {
  isAdmin?: boolean;
};

type SafetyCenterRouteProps = {
  children: React.ReactNode;
};

const SafetyCenterRoute = ({children}: SafetyCenterRouteProps) => {
  const {hasAccess, loading} = useHasSafetyCenterAccess();
  if (loading) return null;
  if (!hasAccess) return <PageNotFound />;
  return <>{children}</>;
};

const InspectionsRoute = ({children}: SafetyCenterRouteProps) => {
  const {hasAccess, loading} = useHasSafetyCenterAccess();
  const [features, featuresLoading] = useFeatures();
  if (loading || featuresLoading) return null;
  if (!hasAccess || !features.TaskChecklists) return <PageNotFound />;
  return <>{children}</>;
};

const ManagerRoot: React.FC<Props> = ({isAdmin}) => {
  useLogAppUse();
  const [features] = useFeatures();
  const [globalFeatures] = useGlobalFeatures();
  const isMobile = useIsMobile('md');
  useTrackPageView();
  const referencingEnabled = useReferencingEnabled();
  const {toggleAll} = useSideBarAccordion();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'e' && e.metaKey) {
        toggleAll();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleAll]);

  useEffect(() => {
    preloadAll(componentsToPreload);
  }, []);

  return (
    <SentryRoutes>
      <Route element={<LayoutSwitch />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/events/:id?/:action?" element={<EventsListPage />} />
        <Route
          path="/documents/:id?/:action?"
          element={<DocumentsListPage />}
        />
        <Route
          path="/updates/:id?/:action?/:communityId?"
          element={<NewsFeedPage />}
        />
        <Route path="/surveys/:id?/:action?" element={<SurveysListPage />} />
        <Route
          path="/messages/:conversationId?"
          element={
            features.ConversationLayout && !isMobile ? (
              <ConversationsListPage />
            ) : (
              <ConversationsPageV2Style />
            )
          }
        />
        <Route
          path="/communities/:communityId/:tab?/:id?/:action?"
          element={<CommunityDetailsPage />}
        />
        <Route path="/communities/" element={<CommunityListPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        {features.Reporting && (
          <>
            <Route
              path="/reporting/site/:communityId?/:tab?"
              element={<SiteReportingPage />}
            />
            <Route path="/reporting/saved" element={<SavedViewsPage />} />
            <Route path="/reporting/saved/:id" element={<SavedViewPage />} />
          </>
        )}
        {features.Reporting && features.PortfolioReporting && (
          <Route
            path="/reporting/portfolio"
            element={<PortfolioReportingPage />}
          />
        )}
        {features.Reporting && features.TeamReporting && (
          <Route path="/reporting/team" element={<TeamReportingPage />} />
        )}
        <Route path="/dashboard/download-rate" element={<DownloadRate />} />
        <Route
          path="/dashboard/resident-engagement"
          element={<ResidentEngagement />}
        />
        <Route
          path="/dashboard/satisfaction-scores"
          element={<SatisfactionScores />}
        />
        <Route path="/dashboard/response-time" element={<ResponseTime />} />
        <Route
          path="/contact-profiles/:contactId?/:dialogType?/:dialogId?/:action?"
          element={<ContactPage />}
        />
        <Route path="/contact-profiles/" element={<ContactsListPage />} />
        {globalFeatures.newApplicationFlow ? (
          <>
            <Route
              path="/verification/:applicationId"
              element={<ApplicantPage />}
            />
            <Route path="/verification" element={<ApplicantsListPage />} />
          </>
        ) : (
          <Route
            path="/verification/:applicationId?"
            element={<ApplicantsListPage />}
          />
        )}
        {features.Contractors && (
          <Route
            path="/contractors/:contractorCompanyId?"
            element={<ContractorsListPage />}
          />
        )}
        <Route path="/profile/:tab?" element={<ProfilePage />} />
        <Route path="/packages/:packageId?" element={<PackagesPage />} />
        <Route
          path="/keys/:keyId?/:action?/:keyholderId?/:action?"
          element={<KeysListPage />}
        />
        <Route
          path="/community-products/:id?"
          element={<CommunityProductsListPage />}
        />
        <Route
          path="/homes/:homeId?/packages/:packageId?"
          element={<HomesPackagesPage />}
        />
        <Route path="/homes/:homeId/:applicationId?" element={<HomePage />} />
        <Route
          path="/homes/:homeId/group/:groupId?/:action?"
          element={<HomePage />}
        />
        <Route path="/homes" element={<HomesListPage />} />
        <Route
          path="/safety-center"
          element={
            <SafetyCenterRoute>
              <Navigate to="/safety-center/records" replace />
            </SafetyCenterRoute>
          }
        />
        <Route
          path="/safety-center/overview"
          element={
            <SafetyCenterRoute>
              <SafetyOverviewListPage />
            </SafetyCenterRoute>
          }
        />
        <Route
          path="/safety-center/overview/:communityId/:tab?/:documentId?"
          element={
            <SafetyCenterRoute>
              <SafetyOverviewPage />
            </SafetyCenterRoute>
          }
        />
        <Route
          path="/safety-center/records"
          element={
            <SafetyCenterRoute>
              <SafetyCenterPage />
            </SafetyCenterRoute>
          }
        />
        <Route
          path="/safety-center/records/:recordId"
          element={
            <SafetyCenterRoute>
              <SafetyCenterRecordPage />
            </SafetyCenterRoute>
          }
        />
        <Route
          path="/safety-center/documents"
          element={
            <SafetyCenterRoute>
              <LegacyDocumentsRedirect />
            </SafetyCenterRoute>
          }
        />
        <Route
          path="/safety-center/documents/:communityId"
          element={
            <SafetyCenterRoute>
              <LegacyDocumentsRedirect />
            </SafetyCenterRoute>
          }
        />
        <Route
          path="/safety-center/documents/:communityId/:documentId"
          element={
            <SafetyCenterRoute>
              <LegacyDocumentsRedirect />
            </SafetyCenterRoute>
          }
        />
        <Route
          path="/safety-center/inspections"
          element={
            <InspectionsRoute>
              <InspectionsPage />
            </InspectionsRoute>
          }
        />
        <Route
          path="/safety-center/inspections/:taskId"
          element={
            <InspectionsRoute>
              <InspectionDetailPage />
            </InspectionsRoute>
          }
        />
        {features.LeaseholderPayments && (
          <Route path="/payments" element={<PaymentsPage />} />
        )}
        <Route path="/groups/:groupId?/:action?" element={<GroupsListPage />} />
        <Route
          path="/facilities/:id?/:action?"
          element={<AmenitiesListPage />}
        />
        <Route
          path="/appointments/:id?/:action?"
          element={<AppointmentsListPage />}
        />
        <Route path="/forms/:id?" element={<FormsListPage />} />
        <Route
          path="/notifications"
          element={
            isMobile ? <NotificationCenterPage /> : <NotificationListPage />
          }
        />
        <Route path="/features/:slug?" element={<FeaturesPage />} />
        {referencingEnabled && (
          <>
            <Route
              path="/referencing/application/:id"
              element={
                <ParamsToUUIDNavigator parameterNames={['id']}>
                  <ApplicationDetails />
                </ParamsToUUIDNavigator>
              }
            />
            <Route
              path="/tenant-reference-report/:tenantReferenceUUID"
              element={
                <ParamsToUUIDNavigator parameterNames={['tenantReferenceUUID']}>
                  <ApplicantDetails />
                </ParamsToUUIDNavigator>
              }
            />
            <Route path="/referencing" element={<Referencing />} />
          </>
        )}
        {features.AutomatedBlocks && (
          <>
            <Route path="/blocks" element={<BlocksListPage />} />
            <Route
              path="/blocks/:id/:tab?/:itemId?/:itemAction?"
              element={<ViewBlockPage />}
            />
            <Route
              path="/blocks/:id/homes/assign"
              element={<BulkAssignHomesPage />}
            />
          </>
        )}
        <Route
          path="/compliance/blocks"
          element={<ComplianceBlocksListPage />}
        />
        <Route
          path="/compliance/requirements/:requirementId?/:action?"
          element={<RequirementsListPage />}
        />
        {isAdmin && <Route path="/members" element={<MemberListPage />} />}
        {isAdmin && <Route path="/members/new" element={<NewMemberPage />} />}
        {isAdmin && (
          <Route path="/members/:id/:tab?" element={<ViewMemberPage />} />
        )}
        {isAdmin && features.Teams && (
          <Route path="/teams" element={<TeamListPage />} />
        )}
        {isAdmin && features.Teams && (
          <Route path="/teams/:teamId/:tab?" element={<ViewTeamPage />} />
        )}
        {isAdmin && features.Teams && (
          <Route path="/teams/new" element={<NewTeamPage />} />
        )}
        {isAdmin && <Route path="/settings/:id?" element={<SettingsPage />} />}
        {isAdmin && <Route path="/tags" element={<TagListPage />} />}
        {isAdmin && <Route path="/tags/:id/:tab?" element={<ViewTagPage />} />}
        <Route
          path="/visitors/qr-codes/:communityId?"
          element={<VisitorsListPage />}
        />
        <Route path="/visitors/:id?" element={<VisitorsListPage />} />
        <Route path="/marketplace/:id?" element={<MarketplaceListPage />} />
        <Route path="/pulse" element={<PulsePage />} />
        {features.Rules && (
          <>
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/rules/new" element={<NewRulePage />} />
            <Route path="/rules/:ruleId/:tab?" element={<ViewRulePage />} />
          </>
        )}
        {features.Tasks && (
          <>
            <Route
              path="/tasks/scheduled/:taskGroupId/:tab?"
              element={<ViewTaskGroupPage />}
            />
            <Route path="/tasks/scheduled" element={<ScheduledTasksPage />} />
            {features.TaskChecklists && (
              <Route
                path="/tasks/templates/:templateId"
                element={<TaskTemplateViewPage />}
              />
            )}
            {features.TaskChecklists && (
              <Route path="/tasks/templates" element={<TaskTemplatesPage />} />
            )}
            <Route path="/tasks/:taskId/:tab?" element={<ViewTaskPage />} />
            <Route path="/tasks" element={<TasksPage />} />
          </>
        )}
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </SentryRoutes>
  );
};

export default ManagerRoot;
